import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useSpinner } from '@/hooks/useSpinner';
import { useToast } from '@/hooks/use-toast';
import {
  Grid3x3,
  List,
  Search,
  Filter,
  X,
  ShoppingBag,
  Clock,
  CheckCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Info,
  Receipt,
  Gift,
  Gem
} from 'lucide-react';
import { diamondsService, type RecompensaItem } from '@/services/diamonds.service';
import { franchiseService } from '@/services/franchise.service';
import { getSubdomainFranchise } from '@/utils/get-subdomain';
import { useAuthContext } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LoadingSpinner from '@/components/LoadingSpinner';
import Swal from 'sweetalert2';

interface SearchFilters {
  nome: string;
  categoria: string;
  valorMin: number | null;
  valorMax: number | null;
}

type ViewMode = 'grid' | 'list';

const LojaRecompensas: React.FC = () => {
  const { isLoading, showSpinner, hideSpinner } = useSpinner();
  const { toast } = useToast();
  const navigate = useNavigate();
  const subdomain = getSubdomainFranchise();

  // Estados principais
  const [saldo, setSaldo] = useState<number>(0);
  const [statusAdesao, setStatusAdesao] = useState<string | null>('');
  const [checkTermsValue, setCheckTermsValue] = useState<boolean>(false);

  // Estados dos itens
  const [itemList, setItemList] = useState<RecompensaItem[]>([]);
  const [filteredItemList, setFilteredItemList] = useState<RecompensaItem[]>([]);
  const [categorias, setCategorias] = useState<Array<{ nome: string; count: number }>>([]);
  const [displayedItems, setDisplayedItems] = useState<RecompensaItem[]>([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Estados da UI
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchFilters, setSearchFilters] = useState<SearchFilters>({
    nome: '',
    categoria: '',
    valorMin: null,
    valorMax: null
  });
  const [showFilters, setShowFilters] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState<Record<number, number>>({});
  const [selectedItem, setSelectedItem] = useState<RecompensaItem | null>(null);
  const [showResgateModal, setShowResgateModal] = useState(false);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [showSaibaMaisModal, setShowSaibaMaisModal] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const user = JSON.parse(localStorage.getItem('userlogged') || 'null');

  // Configurações de paginação
  const isMobile = window.innerWidth <= 768;
  const itemsPerPage = isMobile ? 10 : 20;

  const excecoesCodigosPrograma = ['3905', '9999'];

  // Carregamento inicial
  useEffect(() => {
    initializeComponent();
  }, []);

  // Controle do botão de voltar ao topo
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const initializeComponent = async () => {
    try {
      showSpinner('Aguarde', 'Carregando recompensas...');
      setLoading(true);

      // Verificar franquia suspensa
      const franquiasDisponiveis = await franchiseService.findAllAvailable();
      const franquiaSelecionadaObj = franquiasDisponiveis.find((f: any) => f.subdomain === subdomain);

      if (franquiaSelecionadaObj && franquiaSelecionadaObj.status === 'suspenso') {
        window.location.href = '/authentication/side-login';
        return;
      }

      // Verificar autenticação
      if (!user) {
        await Swal.fire({
          title: 'Ops',
          text: 'Sessão expirada, autentique novamente',
          icon: 'error',
          confirmButtonText: 'OK',
        });
        window.location.href = '/authentication/side-login';
        return;
      }

      await Promise.all([
        getItens(),
        getAdesao(user.id)
      ]);
      setLoading(false);

    } catch (error) {
      console.error('Erro ao inicializar componente:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao carregar dados. Tente novamente.',
        variant: 'destructive'
      });
      setLoading(false);
    } finally {
      hideSpinner();
      setLoading(false);
    }
  };

  const getItens = async () => {
    try {
      const resp = await diamondsService.getItens();
      if (resp) {
        setItemList(resp);
        setFilteredItemList(resp);
        extractCategorias(resp);
        updateDisplayedItems(resp, 1);
      }
    } catch (error) {
      console.error('Erro ao carregar itens:', error);
    }
  };

  const getAdesao = async (userId: number) => {
    try {
      const resp = await diamondsService.getAdesao(userId);
      if (resp && resp[0]) {
        setStatusAdesao(resp[0].status);
        setSaldo(resp[0].diamantes || 0);
      } else {
        setStatusAdesao(null);
      }
    } catch (error) {
      console.error('Erro ao verificar adesão:', error);
    }
  };

  const extractCategorias = (items: RecompensaItem[]) => {
    const categoriasMap = new Map();

    items.forEach((item) => {
      if (item.categoria_nome) {
        const existing = categoriasMap.get(item.categoria_nome);
        if (existing) {
          existing.count += 1;
        } else {
          categoriasMap.set(item.categoria_nome, { nome: item.categoria_nome, count: 1 });
        }
      }
    });

    setCategorias(Array.from(categoriasMap.values()));
  };

  const applyFilters = useCallback(() => {
    const filtered = itemList.filter((item) => {
      const matchesNome = !searchFilters.nome ||
        item.nome.toLowerCase().includes(searchFilters.nome.toLowerCase());

      const matchesCategoria = !searchFilters.categoria ||
        item.categoria_nome === searchFilters.categoria;

      const matchesValorMin = searchFilters.valorMin === null ||
        item.valor >= searchFilters.valorMin;

      const matchesValorMax = searchFilters.valorMax === null ||
        item.valor <= searchFilters.valorMax;

      return matchesNome && matchesCategoria && matchesValorMin && matchesValorMax;
    });

    setFilteredItemList(filtered);
    setCurrentPage(1);
    updateDisplayedItems(filtered, 1);
  }, [itemList, searchFilters]);

  const updateDisplayedItems = (items: RecompensaItem[], page: number) => {
    const startIndex = 0;
    const endIndex = page * itemsPerPage;
    setDisplayedItems(items.slice(startIndex, endIndex));
  };

  const loadMoreItems = () => {
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    updateDisplayedItems(filteredItemList, nextPage);
  };

  const hasMoreItems = displayedItems.length < filteredItemList.length;

  useEffect(() => {
    applyFilters();
  }, [searchFilters, applyFilters]);

  const hasActiveFilters = useMemo(() => {
    return !!(searchFilters.nome ||
      searchFilters.categoria ||
      searchFilters.valorMin !== null ||
      searchFilters.valorMax !== null);
  }, [searchFilters]);

  const clearFilters = () => {
    setSearchFilters({
      nome: '',
      categoria: '',
      valorMin: null,
      valorMax: null
    });
  };

  const aderir = async () => {
    if (!checkTermsValue) {
      toast({
        title: 'Ops!',
        description: 'É necessário aceitar os termos para aderir ao programa.',
        variant: 'destructive'
      });
      return;
    }

    if (!user || ((user.role !== 'revendedora' && user.role !== 'coordenadora' && user.role !== 'admin') &&
      !excecoesCodigosPrograma.includes(user.codigo.toString()))) {
      toast({
        title: 'Ops!',
        description: 'Apenas revendedoras podem aderir ao programa de benefícios.',
        variant: 'destructive'
      });
      return;
    }

    try {
      showSpinner('Aguarde', 'Processando adesão...');
      await diamondsService.aderirPrograma(user.id);

      toast({
        title: 'Sucesso!',
        description: 'Adesão realizada com sucesso! Aguarde aprovação.',
        variant: 'default'
      });

      await getAdesao(user.id);
    } catch (error) {
      console.error('Erro ao aderir:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao processar adesão. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      hideSpinner();
    }
  };

  const openResgateModal = (item: RecompensaItem) => {
    if (!user) return;

    if (statusAdesao !== 'aprovado') {
      toast({
        title: 'Ops!',
        description: 'Você precisa ter adesão aprovada para resgatar itens.',
        variant: 'destructive'
      });
      return;
    }

    if (saldo < item.valor) {
      toast({
        title: 'Saldo insuficiente',
        description: `Você precisa de ${item.valor} diamantes para resgatar este item.`,
        variant: 'default'
      });
      return;
    }

    setSelectedItem(item);
    setShowResgateModal(true);
  };

  const confirmarResgate = async () => {
    if (!selectedItem || !user) return;

    try {
      showSpinner('Aguarde', 'Processando resgate...');
      await diamondsService.resgatar(user.id, selectedItem.id);

      toast({
        title: 'Sucesso!',
        description: 'Item resgatado com sucesso!',
        variant: 'default'
      });

      setShowResgateModal(false);
      setSelectedItem(null);

      await Promise.all([
        getAdesao(user.id),
        getItens() // Atualizar lista para refletir disponibilidade
      ]);
    } catch (error) {
      console.error('Erro ao resgatar:', error);
      toast({
        title: 'Erro',
        description: 'Erro ao processar resgate. Tente novamente.',
        variant: 'destructive'
      });
    } finally {
      hideSpinner();
    }
  };

  // Navegação de imagens
  const getCurrentImage = (item: RecompensaItem): string => {
    const index = currentImageIndex[item.id] || 0;
    const url = 'https://xdoinfinitto.com.br:3000';
    if (index === 0) {
      return `${url}/diamonds/item-image/${item.imagem}`;
    } else {
      return `${url}/diamonds/item-image/${item.aditional_images?.[index - 1].fileUploaded || item.imagem}`;
    }
  };

  const prevImage = (item: RecompensaItem): void => {
    const totalImages = 1 + (item.aditional_images?.length || 0);
    const currentIndex = currentImageIndex[item.id] || 0;
    const newIndex = currentIndex - 1;

    setCurrentImageIndex(prev => ({
      ...prev,
      [item.id]: newIndex < 0 ? totalImages - 1 : newIndex
    }));
  };

  const nextImage = (item: RecompensaItem): void => {
    const totalImages = 1 + (item.aditional_images?.length || 0);
    const currentIndex = currentImageIndex[item.id] || 0;
    const newIndex = currentIndex + 1;

    setCurrentImageIndex(prev => ({
      ...prev,
      [item.id]: newIndex >= totalImages ? 0 : newIndex
    }));
  };

  const saberMais = () => {
    // Implementar modal ou redirecionamento para mais informações
    toast({
      title: 'Em breve',
      description: 'Funcionalidade em desenvolvimento.',
      variant: 'default'
    });
  };

  const infosResgate = () => {
    Swal.fire({
      title: 'Como funciona o resgate?',
      html: `
        <div class="text-left">
          <p><strong>💎 Diamantes:</strong> Ganhe diamantes a cada venda realizada</p>
          <p><strong>🛒 Resgate:</strong> Troque seus diamantes por produtos incríveis</p>
          <p><strong>📦 Entrega:</strong> Enviamos direto para seu endereço</p>
          <p><strong>⏱️ Prazo:</strong> Entrega em até 15 dias úteis</p>
        </div>
      `,
      icon: 'info',
      confirmButtonText: 'Entendi'
    });
  };

  // Componente de Status Header
  const StatusHeader = () => {
    if (statusAdesao === 'pendente') {
      return (
        <Card className="mb-6 border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-800">
              <Clock className="w-5 h-5" />
              Adesão Pendente
            </CardTitle>
            <p className="text-sm text-blue-700">
              Nossa equipe está analisando seu pedido de adesão ao Programa de Benefícios!
              Em breve você poderá ganhar diamantes nas suas vendas e resgatá-los aqui.
            </p>
            <Button variant="outline" onClick={saberMais} className="w-fit mt-2">
              <Info className="w-4 h-4 mr-2" />
              Saber mais
            </Button>
          </CardHeader>
        </Card>
      );
    }

    if (statusAdesao === 'aprovado') {
      return (
        <Card className="mb-6 bg-gradient-to-br from-purple/5 to-purple/10 border-purple/20">
          <CardHeader>
            <span className="text-xs text-gray-600">Saldo disponível:</span>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1">
                <span className={`text-2xl font-bold ${saldo === 0 ? 'text-gray-400' : 'text-purple-600'}`}>
                  {saldo}
                </span>
                <Gem className={`w-6 h-6 ${saldo === 0 ? 'text-gray-400' : 'text-purple-600'}`} />
              </div>
              <div className="flex flex-col gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/extratos-diamantes')}>
                  <Receipt className="w-4 h-4 mr-2" />
                  Ver extratos
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowSaibaMaisModal(true)}>
                  <Info className="w-4 h-4 mr-2" />
                  Saiba mais
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>
      );
    }

    if (statusAdesao === null) {
      return (
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardHeader>
            <CardTitle className="text-purple-800">
              Boas-vindas ao Programa de Benefícios!
            </CardTitle>
            <p className="text-sm text-purple-700 mb-4">
              Ao fazer parte do Programa de Benefícios, suas vendas geram diamantes que podem ser
              resgatados por itens. Clique em aderir e aceite os termos para participar.
              Quanto mais vender, mais diamantes você ganha!
            </p>
            <div className="flex items-center space-x-2 mb-4">
              <Checkbox
                id="terms"
                checked={checkTermsValue}
                onCheckedChange={(checked) => setCheckTermsValue(checked as boolean)}
              />
              <Label htmlFor="terms" className="text-sm">
                Li e aceito os{' '}
                <button className="text-purple-600 underline" onClick={saberMais}>
                  termos e condições
                </button>
              </Label>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={aderir}
                disabled={!checkTermsValue}
                className="bg-green-600 hover:bg-green-700"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Aderir ao programa
              </Button>
              <Button variant="outline" onClick={saberMais}>
                <Info className="w-4 h-4 mr-2" />
                Saber mais
              </Button>
            </div>
          </CardHeader>
        </Card>
      );
    }

    if (statusAdesao === 'reprovado' && user?.role === 'revendedora') {
      return (
        <Card className="mb-6 border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Adesão Reprovada
            </CardTitle>
            <p className="text-sm">
              Nossa equipe analisou seu pedido de adesão ao programa de Benefícios e infelizmente
              não conseguimos ter você conosco agora. Para mais dúvidas entre em contato.
            </p>
            <Button variant="outline" onClick={saberMais} className="w-fit mt-2">
              <Info className="w-4 h-4 mr-2" />
              Saber mais
            </Button>
          </CardHeader>
        </Card>
      );
    }

    return null;
  };

  // Componente de Item em Grid
  const ItemGrid = ({ item }: { item: RecompensaItem }) => {
    const hasMultipleImages = (item.aditional_images?.length || 0) > 0;

    return (
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="relative">
          <div className="carousel-container relative text-center p-6">
            <img
              src={getCurrentImage(item)}
              alt={item.nome}
              className="w-full min-h-48 max-h-56 object-contain transition-transform duration-300"
            />

            {hasMultipleImages && (
              <>
                <button
                  onClick={() => prevImage(item)}
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white border-0 p-2 cursor-pointer rounded-full z-10 hover:bg-opacity-80"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => nextImage(item)}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white border-0 p-2 cursor-pointer rounded-full z-10 hover:bg-opacity-80"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </>
            )}
          </div>

          <div className="absolute top-2 left-2">
            <Badge variant={"outline"} className="text-xs border-gray-300 bg-white/70 text-gray-400">
              {`${item.categoria_nome}`}
            </Badge>
          </div>

          <div className="absolute top-2 right-2">
            <Badge variant={"secondary"}>
              {`${item.qntd_disponivel} ${item.qntd_disponivel == 1 ? 'und disponível' : 'disponíveis'}`}
            </Badge>
          </div>
        </div>

        <CardContent className="p-4 bg-gradient-to-br h-full from-[#ece1cf]/40 to-[#ece1cf]/80">
          {/* <div className="bg-amber-50 p-3 rounded-lg mb-4">
            <h3 className="font-medium text-sm text-amber-800">{item.categoria_nome}</h3>
          </div> */}

          <h4 className="font-semibold mb-2 line-clamp-2">{item.nome}</h4>

          {item.descricao && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.descricao}</p>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <span className="text-xl font-bold text-purple-600">{item.valor}</span>
              <Gem className="w-4 h-4 text-purple-600" />
            </div>

            <Button
              size="sm"
              onClick={() => openResgateModal(item)}
              disabled={item.qntd_disponivel <= 0 || statusAdesao !== 'aprovado'}
              className="bg-green-600 hover:bg-green-700"
            >
              <ShoppingBag className="w-4 h-4 mr-1" />
              Resgatar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Componente de Item em Lista
  const ItemList = ({ item }: { item: RecompensaItem }) => {
    const hasMultipleImages = (item.aditional_images?.length || 0) > 0;

    return (
      <Card className="overflow-hidden hover:shadow-lg transition-shadow">
        <div className="flex">
          <div className="relative w-[45%] flex-shrink-0">
            <div className="carousel-container relative text-center h-full">
              <img
                src={getCurrentImage(item)}
                alt={item.nome}
                className="w-full h-full object-contain"
              />

              {hasMultipleImages && (
                <>
                  <button
                    onClick={() => prevImage(item)}
                    className="absolute left-1 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white border-0 p-1 cursor-pointer rounded-full z-10 hover:bg-opacity-80"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => nextImage(item)}
                    className="absolute right-1 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white border-0 p-1 cursor-pointer rounded-full z-10 hover:bg-opacity-80"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </>
              )}
            </div>

            <div className="absolute top-1 right-1">
              <Badge variant={"secondary"} className="text-xs">
                {`${item.qntd_disponivel} ${item.qntd_disponivel == 1 ? 'und disponível' : 'disponíveis'}`}
              </Badge>
            </div>
          </div>

          <CardContent className="p-4 pl-2 flex items-center w-[55%]">
            <div>
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">              
                  <h4 className="text-xs font-semibold mb-1">{item.nome}</h4>
                  {item.descricao && (
                    <p className="text-xs text-gray-600 line-clamp-2">{item.descricao}</p>
                  )}
                </div>
              </div>           
              <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1">
                  <span className="text-lg font-bold text-purple-600">{item.valor}</span>
                  <Gem className="w-4 h-4 text-purple-600" />
                </div>

                <Button
                  size="sm"
                  onClick={() => openResgateModal(item)}
                  disabled={item.qntd_disponivel <= 0 || statusAdesao !== 'aprovado'}
                  className="bg-green-600 hover:bg-green-700 text-xs"
                >
                  <ShoppingBag className="w-4 h-4 mr-1" />
                  Resgatar
                </Button>
              </div>
            </div>
           
          </CardContent>
        </div>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <LoadingSpinner isLoading={isLoading} />

      <div className="max-w-7xl mx-auto">
        <StatusHeader />

        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CardTitle className='text-lg'>Recompensas para resgatar</CardTitle>
              </div>
            </div>

            <div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  id="search"
                  placeholder="Buscar por nome..."
                  value={searchFilters.nome}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, nome: e.target.value }))}
                  className="pl-10"
                />
              </div>
            </div>

            <div className="flex justify-end items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros
              </Button>

              <div className="flex border rounded-lg p-1">
                <Button
                  variant={viewMode === 'grid' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid3x3 className="w-4 h-4" />
                  {viewMode !== 'grid' && <span>Ver grade</span>}
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="w-4 h-4" />
                  {viewMode !== 'list' && <span>Ver lista</span>}
                </Button>
              </div>
            </div>
          </CardHeader>
        </Card>

        {showFilters && (
          <Card className="mb-4">
            <CardContent className="p-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label htmlFor="categoria">Categoria</Label>
                  <Select
                    value={searchFilters.categoria || ""}
                    onValueChange={(value) => setSearchFilters(prev => ({ ...prev, categoria: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas as categorias" />
                    </SelectTrigger>
                    <SelectContent>
                      {categorias.map((cat) => (
                        <SelectItem key={cat.nome} value={cat.nome}>
                          {cat.nome} ({cat.count})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <div className="flex-1">
                    <Label htmlFor="valorMin">Valor mínimo</Label>
                    <div className="relative">
                      <Input
                        id="valorMin"
                        type="number"
                        placeholder="0"
                        value={searchFilters.valorMin || ''}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          valorMin: e.target.value ? parseInt(e.target.value) : null
                        }))}
                        className="pr-8"
                      />
                      <Gem className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-600" />
                    </div>
                  </div>

                  <div className="flex-1">
                    <Label htmlFor="valorMax">Valor máximo</Label>
                    <div className="relative">
                      <Input
                        id="valorMax"
                        type="number"
                        placeholder="∞"
                        value={searchFilters.valorMax || ''}
                        onChange={(e) => setSearchFilters(prev => ({
                          ...prev,
                          valorMax: e.target.value ? parseInt(e.target.value) : null
                        }))}
                        className="pr-8"
                      />
                      <Gem className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t">
                  <span className="text-sm text-gray-600">
                    {filteredItemList.length} produto(s) encontrado(s)
                  </span>
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    <X className="w-4 h-4 mr-2" />
                    Limpar filtros
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {itemList.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Gift className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              {!loading ? 
                (
                  <p className="text-gray-600">
                    Não temos nenhum benefício disponível para resgate no momento.
                    Continue vendendo para acumular diamantes e resgatar benefícios incríveis!
                  </p>
                )
              
              : (
                  <p className="text-gray-600">Carregando itens da loja de recompensas...</p>
              )}
             
            </CardContent>
          </Card>
        ) : (
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
          }>
            {displayedItems.map((item) => (
              viewMode === 'grid'
                ? <ItemGrid key={item.id} item={item} />
                : <ItemList key={item.id} item={item} />
            ))}
          </div>
        )}

        {hasMoreItems && (
          <div className="text-center mt-8">
            <Button className='border-gray-300 text-accent-foreground hover:bg-background' onClick={loadMoreItems} variant="outline">
              Carregar mais itens
            </Button>
          </div>
        )}

        {/* Modal de Resgate */}
        <Dialog open={showResgateModal} onOpenChange={setShowResgateModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {selectedItem && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-xl font-bold flex items-center gap-2">
                    <ShoppingBag className="w-5 h-5 text-green-600" />
                    Confirmar Resgate
                  </DialogTitle>
                  <DialogDescription>
                    Você está prestes a resgatar este item com seus diamantes
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                  {/* Imagem do Produto */}
                  <div className="relative bg-gray-50 rounded-lg p-6 text-center">
                    <img
                      src={getCurrentImage(selectedItem)}
                      alt={selectedItem.nome}
                      className="max-w-full max-h-64 mx-auto object-contain rounded-lg shadow-sm"
                    />
                    
                    {/* Navegação de imagens no modal */}
                    {(selectedItem.aditional_images?.length || 0) > 0 && (
                      <>
                        <button
                          onClick={() => prevImage(selectedItem)}
                          className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white border-0 p-2 cursor-pointer rounded-full z-10 hover:bg-opacity-80"
                        >
                          <ChevronLeft className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => nextImage(selectedItem)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white border-0 p-2 cursor-pointer rounded-full z-10 hover:bg-opacity-80"
                        >
                          <ChevronRight className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>

                  {/* Informações do Produto */}
                  <div className="space-y-4">
                    <div>
                      <Badge variant="outline" className="mb-2">
                        {selectedItem.categoria_nome}
                      </Badge>
                      <h3 className="text-lg font-semibold text-gray-900">
                        {selectedItem.nome}
                      </h3>
                    </div>

                    {selectedItem.descricao && (
                      <div>
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Descrição:</h4>
                        <p className="text-sm text-gray-600 leading-relaxed">
                          {selectedItem.descricao}
                        </p>
                      </div>
                    )}

                    {/* Card de Informações */}
                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="text-center">
                          <div className="flex items-center justify-center gap-1 mb-1">
                            <Gem className="w-5 h-5 text-purple-600" />
                            <span className="text-lg font-bold text-purple-600">
                              {selectedItem.valor}
                            </span>
                          </div>
                          <p className="text-xs text-purple-700">Custo em diamantes</p>
                        </div>
                        
                        <div className="text-center">
                          <div className="text-lg font-bold text-gray-800 mb-1">
                            {selectedItem.qntd_disponivel}
                          </div>
                          <p className="text-xs text-gray-600">
                            {selectedItem.qntd_disponivel === 1 ? 'Unidade disponível' : 'Unidades disponíveis'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Resumo do Resgate */}
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-amber-800">Seu saldo atual:</span>
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-amber-800">{saldo}</span>
                          <Gem className="w-4 h-4 text-amber-800" />
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-amber-800">Custo do item:</span>
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-amber-800">-{selectedItem.valor}</span>
                          <Gem className="w-4 h-4 text-amber-800" />
                        </div>
                      </div>
                      
                      <hr className="border-amber-300 my-2" />
                      
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-bold text-amber-900">Saldo após resgate:</span>
                        <div className="flex items-center gap-1">
                          <span className="font-bold text-amber-900">
                            {saldo - selectedItem.valor}
                          </span>
                          <Gem className="w-4 h-4 text-amber-900" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Botões de Ação */}
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      variant="outline"
                      onClick={() => setShowResgateModal(false)}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={confirmarResgate}
                      disabled={saldo < selectedItem.valor || isLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      {isLoading ? 'Processando...' : 'Confirmar Resgate'}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Botão de Voltar ao Topo */}
        {showScrollToTop && (
          <Button
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 w-10 h-10 rounded-full bg-primary text-white shadow-lg transition-all duration-300 hover:scale-110"
            size="sm"
          >
            <ChevronUp className="w-5 h-5" />
          </Button>
        )}

        {/* Modal Saiba Mais */}
        <Dialog open={showSaibaMaisModal} onOpenChange={setShowSaibaMaisModal}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Sobre a Loja de Recompensas</DialogTitle>
              <DialogDescription>
                Conheça tudo sobre diamantes, resgate de itens e o Programa de Benefícios
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 text-sm leading-relaxed">
              {/* 1. O que é a Loja de Recompensas? */}
              <div>
                <h3 className="font-semibold text-base mb-2">1. O que é a Loja de Recompensas?</h3>
                <p className="text-gray-700">
                  É o espaço onde você pode trocar seus diamantes acumulados por produtos e itens exclusivos, disponibilizados pela X do Infinito e seus parceiros.
                </p>
              </div>

              {/* 2. O que é um diamante? */}
              <div>
                <h3 className="font-semibold text-base mb-2">2. O que é um diamante?</h3>
                <p className="text-gray-700">
                  Os diamantes são a moeda de benefícios da X do Infinito. Você acumula diamantes através das compras, vendas, indicações e campanhas especiais dentro da plataforma Xizinha, e pode trocá-los por itens na Loja de Recompensas.
                </p>
              </div>

              {/* 3. Os diamantes têm validade? */}
              <div>
                <h3 className="font-semibold text-base mb-2">3. Os diamantes têm validade?</h3>
                <p className="text-gray-700">
                  Sim. Os diamantes têm validade de 24 meses a partir da data de acúmulo. Após esse prazo, expiram automaticamente.
                </p>
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-900 text-xs">
                  💡 <strong>Dica:</strong> acompanhe sempre seu saldo e extrato para não perder nenhum diamante!
                </div>
              </div>

              {/* 4. Quem pode participar? */}
              <div>
                <h3 className="font-semibold text-base mb-2">4. Quem pode participar e acumular diamantes?</h3>
                <p className="text-gray-700">
                  Todas as revendedoras e clientes cadastrados(as) na plataforma Xizinha podem participar do programa e acumular diamantes. Basta realizar compras/vendas ou indicações dentro da plataforma para começar a pontuar automaticamente.
                </p>
              </div>

              {/* 5. Como acumulo diamantes? */}
              <div>
                <h3 className="font-semibold text-base mb-2">5. Como acumulo diamantes?</h3>
                <p className="text-gray-700 mb-2">
                  Você acumula diamantes de diferentes formas, seja como cliente ou revendedora:
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="text-gray-700">💎 A cada R$50,00 pagos pela Xizinha, você ganha 1 diamante;</li>
                  <li className="text-gray-700">💎 Ao indicar clientes e revendedoras que se ativarem, em até 3 níveis;</li>
                  <li className="text-gray-700">💎 Em campanhas e desafios promocionais divulgados nos canais oficiais da X do Infinito.</li>
                </ul>
                <p className="text-gray-700 mt-2">
                  Os diamantes são contabilizados automaticamente no seu saldo dentro da plataforma Xizinha.
                </p>
              </div>

              {/* 6. Como faço para resgatar? */}
              <div>
                <h3 className="font-semibold text-base mb-2">6. Como faço para resgatar um item?</h3>
                <p className="text-gray-700">
                  Escolha o produto desejado na Loja de Recompensas, clique em "Resgatar" e confirme sua solicitação. Seu pedido passará por uma breve análise antes da aprovação e liberação da entrega/retirada.
                </p>
              </div>

              {/* 7. Como sei se meu resgate foi aprovado? */}
              <div>
                <h3 className="font-semibold text-base mb-2">7. Como sei se meu resgate foi aprovado?</h3>
                <p className="text-gray-700">
                  Após resgatar um item, o pedido passa por uma análise interna de até 5 (cinco) dias úteis. Após a análise, você receberá uma notificação por WhatsApp informando se o resgate foi aprovado ou reprovado. Em caso de reprovação, os diamantes são estornados automaticamente para o seu saldo.
                </p>
              </div>

              {/* 8. Meu item foi reprovado */}
              <div>
                <h3 className="font-semibold text-base mb-2">8. Meu item foi reprovado, o que isso significa?</h3>
                <p className="text-gray-700">
                  Quando um resgate é reprovado, significa que ele não foi validado por algum motivo — como falta de saldo, erro no pedido ou indisponibilidade do item. Nesse caso, os diamantes são estornados, retornam automaticamente para o seu saldo, e você será notificado por WhatsApp, para realizar um novo resgate, conforme itens disponíveis.
                </p>
              </div>

              {/* 9. Como recebo meu item resgatado? */}
              <div>
                <h3 className="font-semibold text-base mb-2">9. Como recebo meu item resgatado?</h3>
                <div className="space-y-3">
                  <div>
                    <p className="font-medium text-gray-800">Revendedoras:</p>
                    <p className="text-gray-700 mt-1">
                      o item será enviado sem custo, preferencialmente junto com o próximo kit consignado, quando logisticamente possível. Em casos de itens maiores ou com necessidade de envio separado, o produto poderá ser enviado em entrega independente, também sem custo, desde que a revendedora esteja ativa. Se a revendedora não estiver mais ativa, o envio terá custo, e nossa equipe entrará em contato via WhatsApp para alinhar o envio e realizar a cobrança do frete.
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-800">Clientes:</p>
                    <p className="text-gray-700 mt-1">
                      o item será enviado pela sua revendedora, sem custo, quando logisticamente possível. Caso não seja possível o envio pela revendedora ou ela não esteja mais ativa, o item será enviado diretamente ao cliente, no endereço cadastrado na plataforma, mediante pagamento do frete.
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 mt-2 text-xs italic">
                  Todos os envios seguem conforme os Termos e Regulamento do Programa de Benefícios X do Infinito.
                </p>
              </div>

              {/* 10. Qual o prazo de envio? */}
              <div>
                <h3 className="font-semibold text-base mb-2">10. Qual o prazo de envio?</h3>
                <p className="text-gray-700 mb-2">
                  O prazo pode variar conforme o tipo de item e a forma de envio.
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="text-gray-700">- Itens de parceiros ou de confecção têm prazo de 5 a 15 dias úteis para chegada ao estoque da X do Infinito.</li>
                  <li className="text-gray-700">- Itens que já estão disponíveis em estoque são enviados logo após a aprovação do resgate.</li>
                </ul>
                <p className="text-gray-700 mt-2">
                  Depois disso, o envio ocorre conforme as regras de entrega — podendo variar entre clientes e revendedoras ativas.
                </p>
                <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-blue-900 text-xs">
                  💡 <strong>Por esse motivo,</strong> não há um prazo fixo de entrega, pois ele depende da disponibilidade e logística de cada resgate.
                </div>
              </div>

              {/* 11. Os produtos são limitados? */}
              <div>
                <h3 className="font-semibold text-base mb-2">11. Os produtos são limitados?</h3>
                <p className="text-gray-700">
                  Sim. Cada item possui estoque limitado e pode se esgotar a qualquer momento. As opções disponíveis podem variar conforme a parceria e o período da campanha.
                </p>
              </div>

              {/* 12. O valor em diamantes pode mudar? */}
              <div>
                <h3 className="font-semibold text-base mb-2">12. O valor em diamantes dos itens pode mudar?</h3>
                <p className="text-gray-700">
                  Sim. A quantidade de diamantes necessária para resgatar cada item pode ser ajustada a qualquer momento, conforme variações nos custos de produção, parcerias ou atualizações de preço dos produtos. Sempre confira o valor atualizado antes de confirmar o resgate.
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-4 border-t mt-6">
              <Button
                variant="outline"
                onClick={() => setShowSaibaMaisModal(false)}
                className="flex-1"
              >
                Fechar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LojaRecompensas;